// Accessible dialog primitive. Plan 24.
//
// - `focus-trap-react` keeps keyboard focus inside the dialog while open.
// - `role="dialog"` + `aria-modal="true"` + `aria-labelledby` ensures
//   screen readers announce it correctly; the `<Heading>` inside owns
//   the id we link to.
// - Esc closes via the `keydown` effect; `focus-trap`'s
//   `returnFocusOnDeactivate` restores focus to the opener.
// - Backdrop click closes when `dismissOnBackdrop` is left at its
//   default `true`.
// - Under jsdom the trap is inert — `focus-trap` is gated on
//   `typeof window !== 'undefined'` + an `active` flag we only flip
//   once `isOpen` is stable, matching plan 24's note.

import {
  useEffect,
  useId,
  useRef,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { X } from '../icons';
import { cn } from '../utils/cn';
import { FocusTrapBoundary } from './_FocusTrap';
import { Heading } from './Heading';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Required — the accessible name of the dialog. */
  title: ReactNode;
  /** Optional supporting copy under the heading. */
  description?: ReactNode;
  size?: ModalSize;
  /** Backdrop click closes the dialog. Default `true`. */
  dismissOnBackdrop?: boolean;
  /** Hides the default close button; callers can render their own. */
  hideCloseButton?: boolean;
  /** Wrapping panel className override. */
  className?: string;
  children: ReactNode;
}

const SIZE_CLASSES: Record<ModalSize, string> = {
  sm: 'max-w-md',
  md: 'max-w-xl',
  lg: 'max-w-3xl',
  xl: 'max-w-5xl',
};

export const Modal = ({
  isOpen,
  onClose,
  title,
  description,
  size = 'md',
  dismissOnBackdrop = true,
  hideCloseButton = false,
  className,
  children,
}: ModalProps) => {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);

  // Esc to close. We watch on `document` rather than the panel so the
  // listener works even if focus is temporarily off-panel.
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  // Prevent background scroll while open.
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    // Stop Esc from bubbling past the panel so nested listeners (e.g.
    // dropdowns) don't double-handle it.
    if (e.key === 'Escape') e.stopPropagation();
  };

  const trapActive = isOpen && typeof window !== 'undefined';

  return (
    <AnimatePresence>
      {isOpen && (
        <FocusTrapBoundary
          active={trapActive}
          fallbackFocus={() => panelRef.current ?? document.body}
        >
          <div
            className="fixed inset-0 z-[100] flex items-center justify-center p-6"
            onKeyDown={onKeyDown}
          >
            <motion.div
              key="backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={dismissOnBackdrop ? onClose : undefined}
              className="fixed inset-0 bg-stone-900/60 backdrop-blur-sm"
              aria-hidden="true"
            />
            <motion.div
              key="panel"
              ref={panelRef}
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              role="dialog"
              aria-modal="true"
              aria-labelledby={titleId}
              className={cn(
                'relative bg-stone-50 w-full rounded-[2.5rem] overflow-y-auto max-h-[90vh] shadow-2xl',
                SIZE_CLASSES[size],
                className
              )}
            >
              <div className="p-10">
                <div className="flex justify-between items-start gap-6 mb-6">
                  <div className="space-y-1">
                    <Heading level={3} id={titleId}>
                      {title}
                    </Heading>
                    {description && <p className="text-stone-500 text-sm">{description}</p>}
                  </div>
                  {!hideCloseButton && (
                    <button
                      type="button"
                      onClick={onClose}
                      className="p-2 rounded-full hover:bg-stone-200 text-stone-500 hover:text-stone-900 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-900"
                      aria-label="Close dialog"
                    >
                      <X className="w-5 h-5" aria-hidden="true" />
                    </button>
                  )}
                </div>
                {children}
              </div>
            </motion.div>
          </div>
        </FocusTrapBoundary>
      )}
    </AnimatePresence>
  );
};
